import React, { useState, useEffect } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    Box, 
    Typography,
    Divider,
    IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { RiFileExcelLine, RiCloseLine } from "react-icons/ri";
import dayjs from 'dayjs';
import axios from 'axios';
import { API_BASE_URL } from '../../../App';

const ExportModal = ({ open, onClose }) => {
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs());
    const [stats, setStats] = useState({ count: 0, amount: 0 });
    const [loading, setLoading] = useState(false);

    // Fetch preview stats when dates change
    useEffect(() => {
        if (!open) return;
        
        const fetchPreview = async () => {
            try {
                const startStr = startDate ? startDate.format('YYYY-MM-DD') : '';
                const endStr = endDate ? endDate.format('YYYY-MM-DD') : '';
                const response = await axios.get(`${API_BASE_URL}/api/paidGuests?startDate=${startStr}&endDate=${endStr}`);
                
                let totalAmount = 0;
                let totalCount = 0;
                response.data.forEach(guest => {
                    totalAmount += parseFloat(guest.amount);
                    totalCount += guest.games.length;
                });
                
                setStats({ count: totalCount, amount: totalAmount.toFixed(2) });
            } catch (error) {
                console.error("Preview stats failed:", error);
            }
        };

        const timer = setTimeout(fetchPreview, 300);
        return () => clearTimeout(timer);
    }, [startDate, endDate, open]);

    const handleExport = async () => {
        setLoading(true);
        try {
            let url = `${API_BASE_URL}/api/export-bookings?`;
            if (startDate) url += `startDate=${startDate.format('YYYY-MM-DD')}&`;
            if (endDate) url += `endDate=${endDate.format('YYYY-MM-DD')}&`;
            
            const response = await axios.get(url, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            const now = dayjs().format('YYYYMMDD');
            link.setAttribute('download', `${now}_Gaststunden.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            onClose();
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RiFileExcelLine size={24} color="#1D6F42" />
                    <Typography variant="h6" component="div">Gastgebühren Export</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <RiCloseLine />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Wähle den Zeitraum für den Excel-Export aus.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <DatePicker
                        label="Von"
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                    <DatePicker
                        label="Bis"
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                </Box>

                <Box sx={{ mt: 4, p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                    <Typography variant="overline" display="block" color="text.secondary" sx={{ mb: 1, lineHeight: 1 }}>
                        Vorschau für Zeitraum
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                                {stats.amount} €
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Gesamtbetrag
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                                {stats.count}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Buchungen
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button onClick={onClose} color="inherit">Abbrechen</Button>
                <Button 
                    variant="contained" 
                    color="success" 
                    startIcon={<RiFileExcelLine />}
                    onClick={handleExport}
                    disabled={loading}
                    sx={{ bgcolor: '#1D6F42', '&:hover': { bgcolor: '#155231' } }}
                >
                    {loading ? 'Generiere...' : 'Excel erstellen'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExportModal;
